import React, { useState, useEffect } from 'react';
import { FaDatabase, FaProjectDiagram } from 'react-icons/fa';
import { BsPeopleFill, BsFolder2Open } from 'react-icons/bs';
import { MdArticle, MdBuild, MdPublic, MdLink, MdWorkOutline } from 'react-icons/md';
import { TbScale } from 'react-icons/tb';
import { MdDashboard } from "react-icons/md";
const Sidebar1 = ({
  selectedCategory,
  onCategoryChange,
  selectedDataSets,
  onDataSetChange,
  selectedCountry,
  onCountryChange,
  csvData,
}) => {
  const [selectionOption, setSelectionOption] = useState('All');
  const [listOptions, setListOptions] = useState([]);

  // 🔹 simple layout flag for mobile
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => {
      if (typeof window !== 'undefined') {
        setIsMobile(window.innerWidth <= 768);
      }
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    const key = selectedCategory === 'River Basins' ? 'River basin' : 'Country';
    const names = Array.from(
      new Set(csvData.map((row) => row[key] || row[key.toLowerCase()]).filter(Boolean))
    ).sort();
    setListOptions(names);
  }, [csvData, selectedCategory]);

  const handleCategorySelect = (cat) => {
    if (typeof onCategoryChange === 'function') onCategoryChange(cat);
    setSelectionOption('All');
    onCountryChange('All');
  };

  const handleDataSetToggle = (key) => {
    if (typeof onDataSetChange === 'function') {
      onDataSetChange({ ...selectedDataSets, [key]: !selectedDataSets[key] });
    }
  };

  const handleSelectionOptionChange = (e) => {
    const val = e.target.value;
    setSelectionOption(val);
    if (val === 'All') {
      onCountryChange('All');
    } else {
      onCountryChange(listOptions[0] || '');
    }
  };

  const handleItemSelect = (e) => {
    onCountryChange(e.target.value);
  };

  const renderDataToggle = () => {
    const isBasin = selectedCategory === 'River Basins';

    // base shared items
    const base = [
      { key: 'primary', label: 'Primary Data Sets', icon: <FaDatabase color="#0297A6" /> },
      { key: 'products', label: 'Water Data Products', icon: <FaProjectDiagram color="#22AD7A" /> },
    ];

    // items shown only for Basin
    const basinOnly = [
      { key: 'multiscale', label: 'Multi-Scale', icon: <TbScale color="#e38200" /> },
      { key: 'publications', label: 'Publications', icon: <MdArticle color="#1a73e8" /> },
      { key: 'tools', label: 'Tools (Dashboards)',   icon: <MdDashboard color="#6a1b9a" />  },
      { key: 'tools', label: 'Tools (Other)',   icon: <MdBuild color="#9a1b5f" />  },
    ];

    // items shown for BOTH (shared across Country & Basin)
    const sharedBoth = [
      { key: 'stakeholders', label: 'Stakeholders', icon: <BsPeopleFill color="#e53935" /> },
      {
        key: 'projects',
        label: 'Projects Using DIWASA Products',
        icon: <BsFolder2Open color="#8A2BE2" />,
      },
      { key: 'links', label: 'Links (Portfolio)', icon: <MdLink color="#009688" /> },
      { key: 'usecase', label: 'UseCase', icon: <FaProjectDiagram color="#cc9900" /> },
    ];

    const dataFields = isBasin ? [...base, ...basinOnly, ...sharedBoth] : [...base, ...sharedBoth];

    return dataFields.map((item) => (
      <div
        key={item.key}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: isMobile ? '6px 10px' : '8px 12px',
          fontSize: isMobile ? '15px' : '17px',
          fontWeight: 500,
          backgroundColor: selectedDataSets[item.key] ? '#f1faff' : 'transparent',
          borderRadius: 6,
          marginBottom: 8,
          cursor: 'pointer',
          transition: 'background 0.2s',
        }}
        onClick={() => handleDataSetToggle(item.key)}
      >
        <input
          type="checkbox"
          checked={!!selectedDataSets[item.key]}
          onChange={() => handleDataSetToggle(item.key)}
          style={{
            marginRight: 10,
            transform: isMobile ? 'scale(1.05)' : 'scale(1.2)',
          }}
        />
        <span style={{ marginRight: 10 }}>{item.icon}</span>
        {item.label}
      </div>
    ));
  };

  return (
    <div
      style={{
        width: isMobile ? '100%' : 320,
        padding: isMobile ? '16px 16px 12px' : '24px',
        background: '#ffffff',
        borderRight: isMobile ? 'none' : '1px solid #ddd',
        borderBottom: isMobile ? '1px solid #eee' : 'none',
        fontFamily: 'Segoe UI, sans-serif',
        minHeight: isMobile ? 'auto' : '100vh',
        boxShadow: isMobile ? '0 1px 4px rgba(0,0,0,0.08)' : '2px 0 8px rgba(0,0,0,0.05)',
        boxSizing: 'border-box',
      }}
    >
      <h2
        style={{
          fontSize: isMobile ? '18px' : '22px',
          color: '#0297A6',
          marginBottom: isMobile ? '14px' : '20px',
        }}
      >
        Categories
      </h2>

      {['Country', 'River Basins'].map((cat) => (
        <div
          key={cat}
          style={{
            fontSize: isMobile ? '16px' : '18px',
            fontWeight: 'bold',
            color: '#333',
            marginBottom: isMobile ? 12 : 16,
          }}
        >
          <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <input
              type="radio"
              name="category"
              onChange={() => handleCategorySelect(cat)}
              checked={selectedCategory === cat}
              style={{
                marginRight: 10,
                transform: isMobile ? 'scale(1.05)' : 'scale(1.2)',
              }}
            />
            {cat}
          </label>

          {(cat === 'Country' || cat === 'River Basins') && selectedCategory === cat && (
            <div
              style={{
                marginTop: 10,
                paddingLeft: isMobile ? 4 : 8,
              }}
            >
              <h4
                style={{
                  fontSize: isMobile ? 14 : 16,
                  color: '#22AD7A',
                  marginBottom: 6,
                }}
              >
                Select {cat === 'Country' ? 'Country' : 'Basin'} Option
              </h4>
              <select
                value={selectionOption}
                onChange={handleSelectionOptionChange}
                style={{
                  width: '100%',
                  padding: isMobile ? '6px 8px' : '8px 10px',
                  fontSize: isMobile ? 14 : 15,
                  marginBottom: 10,
                  borderRadius: 4,
                  border: '1px solid #ccc',
                }}
              >
                <option value="All">
                  All {cat === 'Country' ? 'Countries' : 'Basins'}
                </option>
                <option value="Select">
                  Select Specific {cat === 'Country' ? 'Country' : 'Basin'}
                </option>
              </select>

              {selectionOption === 'Select' && (
                <>
                  <h4
                    style={{
                      fontSize: isMobile ? 14 : 16,
                      color: '#22AD7A',
                      marginBottom: 4,
                    }}
                  >
                    Choose {cat === 'Country' ? 'Country' : 'Basin'}
                  </h4>
                  <select
                    value={selectedCountry}
                    onChange={handleItemSelect}
                    style={{
                      width: '100%',
                      padding: isMobile ? '6px 8px' : '8px 10px',
                      fontSize: isMobile ? 14 : 15,
                      marginBottom: 12,
                      borderRadius: 4,
                      border: '1px solid #ccc',
                    }}
                  >
                    {listOptions.length === 0 ? (
                      <option value="">No options available</option>
                    ) : (
                      listOptions.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))
                    )}
                  </select>
                </>
              )}
            </div>
          )}
        </div>
      ))}

      {selectedCategory && (
        <>
          <h3
            style={{
              fontSize: isMobile ? '17px' : '20px',
              color: '#0297A6',
              marginTop: isMobile ? 16 : 20,
              marginBottom: isMobile ? 10 : 14,
            }}
          >
            Data Information
          </h3>
          {renderDataToggle()}
        </>
      )}
    </div>
  );
};

export default Sidebar1;
